/**
 * In-flight dedup for farming-unit and field history loads.
 *
 * Several call sites (selected_entities, screen mount effects, report screens)
 * request the same history in one tick. Each key shares one request; a forced
 * load (after a write or a units change) starts a new one and becomes the only
 * request allowed to write its response, so an older response that lands
 * later is dropped instead of overwriting fresher data.
 *
 * Kept outside Redux on purpose: the dedup check must not depend on dispatch
 * ordering or on the load status that the UI reads.
 */

const pendingFarmingUnitHistoryLoads = {};
const pendingFieldHistoryLoads = {};

/**
 * Share one in-flight load per key. start(isCurrent) runs only for the first
 * caller (or for a forced caller) and must return a promise; isCurrent()
 * stays true until a forced load for the same key replaces this one.
 */
function runHistoryLoad(pendingMap, key, force, start) {
  const existing = pendingMap[key];
  if (existing && !force) {
    return existing.promise;
  }

  const entry = {};
  const isCurrent = () => pendingMap[key] === entry;
  pendingMap[key] = entry;
  entry.promise = Promise.resolve(start(isCurrent)).finally(() => {
    if (pendingMap[key] === entry) {
      delete pendingMap[key];
    }
  });
  return entry.promise;
}

/**
 * Forget every in-flight load (logout / distribution data reset). Their late
 * responses are then no longer current and get dropped, and the next caller
 * starts a fresh request instead of joining one from the previous session.
 */
function clearPendingHistoryLoads() {
  [pendingFarmingUnitHistoryLoads, pendingFieldHistoryLoads].forEach(
    pendingMap => {
      Object.keys(pendingMap).forEach(key => {
        delete pendingMap[key];
      });
    }
  );
}

module.exports = {
  runHistoryLoad,
  clearPendingHistoryLoads,
  pendingFarmingUnitHistoryLoads,
  pendingFieldHistoryLoads,
};
